/**
 * Datos de Trabajadores (T027 · US1, implementation-notes §4 · wiring §7.1/§7.2).
 *
 * Clave de query `['workers']` con los filtros como sufijo; la paginación es por cursor opaco
 * (`Page<Worker>` del contrato), igual que la auditoría. Cada respuesta se valida en runtime con
 * el esquema Zod de `@fermin/contracts`.
 *
 * Excepción del CSV: `api.*` siempre parsea JSON, así que `GET /workers/export` (text/csv) se pide
 * con `fetch` directo y `credentials: 'include'` — es la única llamada del Admin que no pasa por
 * `api.*` (wiring §7.1 lo autoriza expresamente para este endpoint). Por eso reutiliza `notify`
 * del cliente para emitir el evento de sesión caída.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  CreateInvitationResponseSchema,
  ErrorEnvelopeSchema,
  WorkerListResponseSchema,
  WorkerSchema,
  type CreateInvitationBody,
  type CreateInvitationResponse,
  type CreateWorkerBody,
  type DeactivateWorkerBody,
  type RevokeDeviceBody,
  type UpdateWorkerBody,
  type Worker,
  type WorkerExportQuery,
  type WorkerListResponse,
} from '@fermin/contracts';

import { ADMIN_API_PREFIX, ApiError, api, getApiBaseUrl, notify } from '../../api/client';

export const WORKERS_QUERY_KEY = ['workers'] as const;

/** Filtros compartidos por el listado y el export (`WorkerExportQuery` del contrato). */
export type WorkerFilters = WorkerExportQuery;

export const EMPTY_WORKER_FILTERS: WorkerFilters = {};

function filterParams(filters: WorkerFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) {
    params.set('q', filters.q);
  }
  if (filters.site_id) {
    params.set('site_id', filters.site_id);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  return params;
}

/** `GET /workers?q&site_id&status&cursor` — el alcance por sede lo aplica la API, nunca el cliente. */
export function workersPath(filters: WorkerFilters = {}, cursor?: string): string {
  const params = filterParams(filters);
  if (cursor) {
    params.set('cursor', cursor);
  }
  const query = params.toString();
  return query ? `/workers?${query}` : '/workers';
}

/** `GET /workers/export?q&site_id&status` (mismos filtros, sin paginación). */
export function workersExportPath(filters: WorkerFilters = {}): string {
  const query = filterParams(filters).toString();
  return query ? `/workers/export?${query}` : '/workers/export';
}

export function fetchWorkersPage(
  filters: WorkerFilters = {},
  cursor?: string,
): Promise<WorkerListResponse> {
  return api.get(workersPath(filters, cursor), WorkerListResponseSchema);
}

export function useWorkers(
  filters: WorkerFilters = {},
): UseInfiniteQueryResult<{ pages: WorkerListResponse[] }, ApiError> {
  return useInfiniteQuery<
    WorkerListResponse,
    ApiError,
    { pages: WorkerListResponse[] },
    unknown[],
    string | null
  >({
    queryKey: [...WORKERS_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => fetchWorkersPage(filters, pageParam ?? undefined),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
}

/** Aplana las páginas cargadas en una sola lista. */
export function flattenWorkerPages(data: { pages: WorkerListResponse[] } | undefined): Worker[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/** Invalida los listados de trabajadores (y `['me']`: los avisos del shell dependen de ellos). */
function useInvalidateWorkers() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: WORKERS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  };
}

export function useCreateWorker() {
  const invalidate = useInvalidateWorkers();
  return useMutation<Worker, ApiError, CreateWorkerBody>({
    mutationFn: (body) => api.post('/workers', body, WorkerSchema),
    onSuccess: invalidate,
  });
}

/** `PATCH /workers/{id}` — el formulario en modo `edit` lo usa (US4 reutiliza este hook). */
export function useUpdateWorker() {
  const invalidate = useInvalidateWorkers();
  return useMutation<Worker, ApiError, { id: string; body: UpdateWorkerBody }>({
    mutationFn: ({ id, body }) => api.patch(`/workers/${id}`, body, WorkerSchema),
    onSuccess: invalidate,
  });
}

export interface CreateInvitationVars {
  workerId: string;
  body?: CreateInvitationBody;
}

/** `POST /workers/{id}/invitations` — devuelve el enlace una sola vez (nunca se vuelve a mostrar). */
export function useCreateInvitation() {
  const invalidate = useInvalidateWorkers();
  return useMutation<CreateInvitationResponse, ApiError, CreateInvitationVars>({
    mutationFn: ({ workerId, body }) =>
      api.post(`/workers/${workerId}/invitations`, body ?? {}, CreateInvitationResponseSchema),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Export CSV (única llamada con `fetch` directo, wiring §7.1)
// ---------------------------------------------------------------------------

/** Nombre de archivo si la respuesta no trajera `Content-Disposition`. */
export const EXPORT_FILENAME_FALLBACK = 'trabajadores.csv';

/** `attachment; filename="trabajadores-20260918.csv"` → `trabajadores-20260918.csv`. */
export function filenameFromContentDisposition(header: string | null): string {
  if (!header) {
    return EXPORT_FILENAME_FALLBACK;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Cabecera mal formada: seguimos con las otras variantes.
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header)?.[1];
  if (quoted) {
    return quoted;
  }
  const bare = /filename=([^;]+)/i.exec(header)?.[1];
  return bare ? bare.trim() : EXPORT_FILENAME_FALLBACK;
}

/** Construye el `ApiError` del catálogo a partir de una respuesta no-2xx del export. */
async function exportError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  const parsed = ErrorEnvelopeSchema.safeParse(payload);
  const requestId = response.headers.get('X-Request-Id') ?? '';
  if (parsed.success) {
    const { code, message, details, request_id } = parsed.data.error;
    return new ApiError({
      code,
      message,
      status: response.status,
      details: details as Record<string, unknown> | undefined,
      requestId: request_id || requestId,
    });
  }
  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: 'Ocurrió un error inesperado. Inténtalo nuevamente.',
    status: response.status,
    requestId,
  });
}

/**
 * Descarga el CSV con los filtros actuales y devuelve el nombre del archivo guardado.
 * El navegador recibe un blob y un `<a download>` sintético: no se abre pestaña ni se pierde
 * la sesión (la cookie viaja con `credentials: 'include'`).
 */
export async function downloadWorkersCsv(filters: WorkerFilters = {}): Promise<string> {
  const response = await fetch(
    `${getApiBaseUrl()}${ADMIN_API_PREFIX}${workersExportPath(filters)}`,
    { credentials: 'include', headers: { Accept: 'text/csv' } },
  );

  if (!response.ok) {
    const error = await exportError(response);
    // Este `fetch` no pasa por `api.*`, así que el evento de sesión caída (401 `UNAUTHENTICATED`)
    // hay que emitirlo a mano: sin él la descarga fallaba en silencio y el shell no llevaba a
    // `/ingresar`.
    notify(error);
    throw error;
  }

  const blob = await response.blob();
  const filename = filenameFromContentDisposition(response.headers.get('Content-Disposition'));
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return filename;
}

// ---------------------------------------------------------------------------
// Perfil del trabajador (T044 · US4 y T048 · US5, wiring §4.4/§4.5)
// ---------------------------------------------------------------------------

/**
 * `GET /workers/{id}` — el perfil completo (dispositivo, invitación más reciente y conteo de
 * solicitudes de re-vinculación). Un trabajador de otra empresa o fuera del `site_scope` responde
 * `404 NOT_FOUND`, indistinguible de un id inventado (FR-022/FR-024): la pantalla muestra
 * "No encontramos este recurso".
 *
 * La clave `['workers','detail',id]` cuelga de `WORKERS_QUERY_KEY`, así que cualquier mutación de
 * trabajadores la invalida junto con los listados.
 */
export function useWorker(id: string): UseQueryResult<Worker, ApiError> {
  return useQuery<Worker, ApiError>({
    queryKey: [...WORKERS_QUERY_KEY, 'detail', id],
    queryFn: () => api.get(`/workers/${id}`, WorkerSchema),
  });
}

export interface WorkerReasonVars {
  id: string;
  body?: RevokeDeviceBody | DeactivateWorkerBody;
}

/** `POST /workers/{id}/device/revoke` — devuelve el trabajador ya con el dispositivo `REVOKED`. */
export function useRevokeDevice() {
  const invalidate = useInvalidateWorkers();
  return useMutation<Worker, ApiError, WorkerReasonVars>({
    mutationFn: ({ id, body }) =>
      api.post(`/workers/${id}/device/revoke`, body ?? {}, WorkerSchema),
    onSuccess: invalidate,
  });
}

/** `POST /workers/{id}/deactivate` — la API revoca en cascada dispositivo, sesión e invitaciones. */
export function useDeactivateWorker() {
  const invalidate = useInvalidateWorkers();
  return useMutation<Worker, ApiError, WorkerReasonVars>({
    mutationFn: ({ id, body }) => api.post(`/workers/${id}/deactivate`, body ?? {}, WorkerSchema),
    onSuccess: invalidate,
  });
}

/** `POST /workers/{id}/reactivate` — vuelve a `PENDING_ACTIVATION` (necesita nueva invitación). */
export function useReactivateWorker() {
  const invalidate = useInvalidateWorkers();
  return useMutation<Worker, ApiError, string>({
    mutationFn: (id) => api.post(`/workers/${id}/reactivate`, undefined, WorkerSchema),
    onSuccess: invalidate,
  });
}
